/**
 * src/pages/CriticalThinking/sections/CommonMistakesSection.tsx
 * Section 7: Common Mistakes, Final Mixed Challenge, and Related Links.
 * Features:
 * - 6 widespread cognitive traps in critical thinking
 * - Final mixed challenge with explained feedback
 * - Comprehensive RelatedLinks to Logic Laboratory, Fallacy Library,
 *   Knowledge Laboratory, Philosophy as Investigation, Glossary & References,
 *   and Simulation Theory.
 */

import React, { useState } from "react";
import { KSection } from "@/components/knowledge/KSection";
import { LessonStep } from "@/components/knowledge/LessonStep";
import { Feedback } from "@/components/knowledge/Feedback";
import { RelatedLinks } from "@/components/knowledge/RelatedLinks";
import { Term } from "@/components/knowledge/Term";
import { FINAL_CHALLENGE, type FinalChallengeQuestion } from "../data/belief.data";
import { Check, RotateCcw, Target, Sparkles } from "lucide-react";
export function CommonMistakesSection() {
  const [challengeAnswers, setChallengeAnswers] = useState<Record<string, string>>({});
  const [showChallengeResults, setShowChallengeResults] = useState(false);
  const handleSelectAnswer = (questionId: string, optionId: string) => {
    setChallengeAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };
  const handleResetChallenge = () => {
    setChallengeAnswers({});
    setShowChallengeResults(false);
  };
  return <KSection id="common-mistakes" eyebrow="Diagnostic Review" title="Common Pitfalls & Master Challenge" intro="Even practiced thinkers fall into predictable cognitive traps. Review the six most pervasive intellectual vulnerabilities before testing your diagnostic acumen on the master challenge.">
      <div className="space-y-8">
        {/* Six Critical Pitfalls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[{
          title: "1. Confusing Tone with Truth",
          desc: "Assuming that someone who sounds calm and authoritative must be correct, while dismissing someone passionate or angry. Polite falsehoods are still false; agitated truths are still true."
        }, {
          title: "2. The Fallacy Fallacy",
          desc: "Assuming that because an opponent committed a formal or informal fallacy in their argument, their conclusion is automatically refuted. Bad reasoning does not prove an assertion false; it merely leaves it unsupported by that speaker."
        }, {
          title: "3. Weaponized Skepticism",
          desc: "Demanding infinite, unattainable levels of proof from claims you dislike while waving through assertions that flatter your existing worldview without asking for a shred of evidence."
        }, {
          title: "4. Equivocation on 'Theory'",
          desc: "Dismissing well-substantiated scientific frameworks by saying 'it's just a theory', conflating the everyday colloquial meaning of a hunch with the rigorous scientific meaning of an explanatory model validated by empirical prediction."
        }, {
          title: "5. Confusing 'Not Established' with 'Disproven'",
          desc: "Declaring a hypothesis dead when the honest answer is that available data is simply insufficient to decide. Refusing to guess is an intellectual skill."
        }, {
          title: "6. Attacking the Person (Ad Hominem)",
          desc: "Rejecting a claim by psychoanalyzing the speaker's hidden motives, pedigree, or political affiliations rather than dissecting the logical validity of their premises and evidence."
        }].map((item, idx) => <div key={idx} className="p-4 rounded-lg border border-border/60 bg-card/40 flex flex-col justify-start">
              <span className="font-serif text-sm font-bold text-foreground mb-1.5 text-primary">
                {item.title}
              </span>
              <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                {item.desc}
              </p>
            </div>)}
        </div>

        {/* Master Challenge */}
        <div className="p-5 sm:p-6 rounded-xl border border-primary/50 bg-card/60 space-y-6">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Target className="size-5 text-primary" />
              <h3 className="font-serif text-lg font-bold text-foreground">
                Master Diagnostic Challenge
              </h3>
            </div>
            <button type="button" onClick={handleResetChallenge} className="text-xs font-mono text-muted-foreground hover:text-foreground flex items-center gap-1.5 cursor-pointer min-h-[44px]">
              <RotateCcw className="size-3.5" />
              <span>Reset Challenge</span>
            </button>
          </div>

          <div className="space-y-6">
            {FINAL_CHALLENGE.map((q, qIdx) => {
            const selectedOptionId = challengeAnswers[q.id];
            return <div key={q.id} className="p-4 rounded-lg border border-border/50 bg-card/30 space-y-3">
                  <span className="font-mono text-xs text-primary font-bold">
                    Problem {qIdx + 1} of {FINAL_CHALLENGE.length}
                  </span>
                  <p className="font-sans text-xs sm:text-sm text-foreground/90 italic bg-card/50 p-3 rounded border border-border/30">
                    "{q.scenario}"
                  </p>
                  <p className="font-serif text-sm font-semibold text-foreground">
                    {q.question}
                  </p>

                  <div className="space-y-2">
                    {q.options.map(opt => {
                  const isSelected = selectedOptionId === opt.id;
                  return <button key={opt.id} type="button" onClick={() => handleSelectAnswer(q.id, opt.id)} className={`crit-option-btn ${isSelected ? "selected" : ""}`}>
                          <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                            {isSelected && <Check className="size-3 text-primary" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-foreground">{opt.text}</p>
                            {showChallengeResults && isSelected && <p className={`text-xs mt-1.5 font-mono ${opt.isCorrect ? "text-primary font-bold" : "text-amber-400"}`}>
                                {opt.feedback}
                              </p>}
                          </div>
                        </button>;
                })}
                  </div>
                </div>;
          })}
          </div>

          <div className="pt-2">
            <button type="button" onClick={() => setShowChallengeResults(true)} className="ql-neon-btn px-6 py-3 text-xs sm:text-sm flex items-center gap-2 cursor-pointer">
              <Sparkles className="size-4" />
              <span>Evaluate My Diagnoses & Show Feedback</span>
            </button>
          </div>
        </div>

        {/* Connected Inquiries Cross-References */}
        <RelatedLinks title="Connected Architecture Domains" items={[{
        to: "/LogicLaboratoryPage#argument-tester",
        label: "Logic Laboratory: Argument Structure",
        why: "Test formal validity, deductive soundness, truth tables, and syllogisms when premises strictly entail conclusions."
      }, {
        to: "/FallacyLibraryPage",
        label: "Fallacy Library: 54 Reasoning Missteps",
        why: "Explore full specimens of informal fallacies, cognitive biases, probabilistic errors, and causal confusions."
      }, {
        to: "/KnowledgeLaboratoryPage",
        label: "Knowledge Laboratory: Epistemology",
        why: "Investigate theories of justified belief, the Gettier challenge, foundationalism vs coherentism, and epistemic virtue."
      }, {
        to: "/PhilosophyAsInvestigationPage",
        label: "Philosophy as Investigation",
        why: "Learn conceptual analysis, thought experiments, reflective equilibrium, and core philosophical methods."
      }, {
        to: "/GlossaryAndReferencesPage#term-fallibilism",
        label: "Glossary: Core Epistemic Terminology",
        why: "Reference precise definitions for fallibilism, principle of charity, burden of proof, steelmanning, and deduction."
      }, {
        to: "/SimulationTheoryPage",
        label: "Simulation Theory: An Epistemic Case Study",
        why: "Analyze Bostrom's trilemma as a master exercise in trilemma deduction, probabilistic priors, and skepticism."
      }]} />
      </div>
    </KSection>;
}